import React, { useState, useEffect } from 'react';
import { LuCalendarCheck } from 'react-icons/lu';
import { useTheme } from '../contexts/ThemeContext';
import useSchoolStore from '../store/schoolStore';
import useUserStore from '../store/userStore';
import useAttendanceStore from '../store/attendanceStore';
import renderGraph from '../components/chat/formatters/GraphFormatter';
import { getClassName } from '../utils/schoolUtils';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';
import Notice from '../components/common/Notice';
import LoadingMessage from '../components/common/LoadingMessage';
import { Field, Select } from '../components/common/FormControls';

/**
 * Full-year attendance of one class and section: the year's percentage and a month-by-month chart.
 * Data comes from useAttendanceStore.getFullYearAttendanceReportForClass.
 */
const AttendancePage = () => {
  const theme = useTheme();

  const { schoolData, classes, getClasses, isClassesLoading, schoolId } = useSchoolStore();
  const { currentUser } = useUserStore();
  const {
    getFullYearAttendanceReportForClass,
    attendanceReport,
    isLoading: isReportLoading,
    error: reportError,
    clearAttendanceReport
  } = useAttendanceStore();

  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSec, setSelectedSec] = useState('');

  useEffect(() => {
    getClasses();
    return () => clearAttendanceReport();
  }, [getClasses, clearAttendanceReport]);

  const handleGetReport = async () => {
    if (!selectedClass || !selectedSec) return;

    await getFullYearAttendanceReportForClass({
      academicYear: schoolData.currentAcademicYear,
      schoolId: schoolData.id,
      userId: currentUser?.userId || '',
      Class: parseInt(selectedClass),
      sec: selectedSec,
      isFullYearReport: true
    });
  };

  const selectedClassData = classes?.find(classItem => classItem.Class.toString() === selectedClass);
  const availableSections = selectedClassData?.sections || [];
  const hasReport = attendanceReport && attendanceReport.length > 0;

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="Attendance"
        subtitle={`Full-year attendance of a class${schoolData?.currentAcademicYear ? ` for ${schoolData.currentAcademicYear}` : ''}.`}
      />

      <Card className="mb-5">
        <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[1fr_1fr_auto]">
          <Field label="Class">
            <Select
              value={selectedClass}
              onChange={(event) => {
                setSelectedClass(event.target.value);
                setSelectedSec('');
              }}
              disabled={isClassesLoading}
            >
              <option value="">{isClassesLoading ? 'Loading classes…' : 'Select class'}</option>
              {classes?.map(classItem => (
                <option key={classItem.Class} value={classItem.Class}>
                  {getClassName(schoolId, classItem.Class)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Section">
            <Select
              value={selectedSec}
              onChange={(event) => setSelectedSec(event.target.value)}
              disabled={!selectedClass || isClassesLoading}
            >
              <option value="">{!selectedClass ? 'Select class first' : 'Select section'}</option>
              {availableSections.map(section => (
                <option key={section} value={section}>{section}</option>
              ))}
            </Select>
          </Field>

          <Button onClick={handleGetReport} disabled={!selectedClass || !selectedSec} loading={isReportLoading}>
            Show attendance
          </Button>
        </div>
      </Card>

      {reportError && (
        <Notice tone="error" title="Could not load the attendance" className="mb-5">
          {reportError}
        </Notice>
      )}

      {isReportLoading && (
        <Card>
          <LoadingMessage message="Preparing the attendance report…" />
        </Card>
      )}

      {hasReport && !isReportLoading && attendanceReport.map((report, reportIndex) => (
        <div key={`report-${reportIndex}`} className="mb-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
          <Card title={`${getClassName(schoolId, report.Class)} ${report.sec}`} subtitle="Whole academic year">
            <p className="text-xs font-medium text-ui-ink-3">Attendance</p>
            <p className="mt-0.5 text-4xl font-semibold tracking-tight text-ui-ink tabular-nums">{report.attendancePercent}%</p>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-ui-muted" aria-hidden>
              <div className="h-full rounded-full bg-brand-500" style={{ width: `${Math.min(Number(report.attendancePercent) || 0, 100)}%` }} />
            </div>
            <p className="mt-4 text-xs font-medium text-ui-ink-3">Students in the class</p>
            <p className="mt-0.5 text-lg font-semibold text-ui-ink tabular-nums">{report.totalStudents}</p>
          </Card>

          <Card title="Month by month" subtitle="Attendance percentage for each month" className="lg:col-span-2">
            {report.monthlyReports && report.monthlyReports.length > 0 ? (
              renderGraph({
                type: 'line',
                title: '',
                labels: report.monthlyReports.map(monthReport => monthReport.monthName),
                datasets: [
                  {
                    label: 'Attendance %',
                    data: report.monthlyReports.map(monthReport => monthReport.attendancePercent)
                  }
                ],
                options: {
                  yAxisTitle: 'Attendance %',
                  xAxisTitle: 'Months'
                }
              }, theme)
            ) : (
              <EmptyState title="No monthly figures yet" message="Monthly attendance appears once attendance has been taken." />
            )}
          </Card>
        </div>
      ))}

      {!attendanceReport && !isReportLoading && !reportError && (
        <Card>
          <EmptyState
            icon={<LuCalendarCheck />}
            title="Choose a class and section"
            message="Pick a class and a section above, then press “Show attendance”."
          />
        </Card>
      )}
    </div>
  );
};

export default AttendancePage;
